import {
  ADD_OBJECT,
  ADD_PRODUCT,
  ADD_DIMENSION,
  ADD_DOOR,
  ADD_UTILITY_BOX,
  ADD_OBSTRUCTION,
  ADD_COMFORT_ZONE,
} from '../objects/action_types'
import { DELETE_SELECTED_OBJECTS, DESELECT_OBJECTS, SELECT_OBJECTS } from '../selectedObjects/action_types'
import { SET_CURRENT_LAYER } from '../layers'
import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Tools } from '~/config/tools'
import { Obstruction } from '~/store/objects/types'
import { DetailedObstruction } from '~/config/detailedObstructions'
import { DoorProduct } from '~/config/doorProducts'
import { UtilityBoxConfig } from '~/config/utilityBoxes'
import { ProductToolProps } from '~/store/tools/types'
import { Door } from '~/config/doors'

export type SetActiveToolPayload =
  | {
      tool: 'OBSTRUCTION_TOOL'
      props: DetailedObstruction
    }
  | {
      tool: 'DOOR_PRODUCT_TOOL'
      props: DoorProduct
    }
  | {
      tool: 'UTILITY_BOX_TOOL'
      props: UtilityBoxConfig
    }
  | {
      tool: 'PRODUCT_TOOL'
      props: ProductToolProps
    }
  | {
      tool: 'DOOR_TOOL'
      props: Door
    }
  | {
      tool: Tools
      props?: never
    }

type ToolState = {
  activeTool: Tools
  activeToolProps: Record<string, unknown>
  isSnapEnabled: boolean
  isOrthoModeEnabled: boolean
  isContinuousModeEnabled: boolean
}

export const initialState: ToolState = {
  activeTool: 'SELECT_TOOL',
  activeToolProps: {},
  isSnapEnabled: true,
  isOrthoModeEnabled: false,
  isContinuousModeEnabled: false,
}

export const toolsSlice = createSlice({
  name: 'tools',
  initialState,
  reducers: {
    setActiveTool(state, { payload }: PayloadAction<SetActiveToolPayload>) {
      state.activeTool = payload.tool
      if (payload.props) {
        state.activeToolProps = payload.props
      }
    },
    setSnapToggle(state) {
      state.isSnapEnabled = !state.isSnapEnabled
    },
    setContinuousModeToggle(state) {
      state.isContinuousModeEnabled = !state.isContinuousModeEnabled
    },
    setOrthoModeToggle(state) {
      state.isOrthoModeEnabled = !state.isOrthoModeEnabled
    },
  },
  extraReducers(builder) {
    builder
      .addCase(SET_CURRENT_LAYER, state => {
        state.activeTool = 'SELECT_TOOL'
        state.activeToolProps = {}
      })
      .addCase(createAction<{ obstruction: Obstruction }>(ADD_OBSTRUCTION), (state, { payload }) => {
        const obstructionType = payload.obstruction.obstructionType
        const isBasicObstruction = obstructionType === 'basic'
        if (!isBasicObstruction && state.isContinuousModeEnabled) return
        state.activeTool = 'SELECT_TOOL'
        state.activeToolProps = {}
      })
      .addCase(createAction<{ tool: Tools }>(DESELECT_OBJECTS), (state, { payload }) => {
        state.activeToolProps = {}
        state.activeTool = payload.tool || 'SELECT_TOOL'
      })
      .addMatcher(
        action => action.type === ADD_PRODUCT,
        state => {
          if (state.isContinuousModeEnabled) return
          state.activeTool = 'SELECT_TOOL'
          state.activeToolProps = {}
        }
      )
      .addMatcher(
        action => [ADD_OBJECT, ADD_DIMENSION].includes(action.type),
        state => {
          state.activeTool = 'SELECT_TOOL'
          state.activeToolProps = {}
        }
      )
      .addMatcher(
        action =>
          [ADD_DOOR, ADD_UTILITY_BOX, ADD_COMFORT_ZONE, SELECT_OBJECTS, DELETE_SELECTED_OBJECTS].includes(action.type),
        state => {
          state.activeTool = 'SELECT_TOOL'
          state.activeToolProps = {}
        }
      )
  },
})
